import { Flex, Form, InputTextArea, CellulaFormSubmitData, Label, Sheet, TextLink, BadgeText } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { TopBar } from '@component/top_bar.component';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { Title } from '@component/title.component';
import { BasketService } from '../basket.service';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { withTranslation } from '@application/i18n/with_translation';
import { CreateNewMessageController, CreateNewMessageFlowData } from '../view_create_new_message.controller';
import { FileUpload } from '@feature/file_manager/file_upload.component';
import { ButtonArrange } from '@component/button_arrange.component';
import { FileManagerService, FileManagerServiceReadyState } from '@feature/file_manager/mod';
import { validateRequired } from '@application/validators';
import { NonRecoverableError, ScreenConclusionController } from './screen_conclusion.controller';
import { Payment } from '@feature/payment/payment.component';
import { useState } from 'react';
import { Async } from '@component/async.component';
import styled from 'styled-components';
import type { CreateNewMessageSettings } from '../create_new_message_settings.service';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { IframeSheet } from '@component/iframe_sheet.component';
import { DataList } from '@component/data_list.component';

const StyledBottomSection = styled(Flex)`
	text-align: center;
	font: ${tokens.text.label.smallRegular};

	${tokens.breakpointQuery.small} {
		text-align: left;
	}
`;

const REASON_MAX_LENGTH = 4000;

export const ScreenConclusion = withNewContainer(function ScreenConclusion(): JSX.Element | null {
	const createNewMessageController = useResolveController(CreateNewMessageController);

	return (
		<Async derive={createNewMessageController.flowSettings}>
			{(flowSettings) => (
				<Async derive={createNewMessageController.flowData}>
					{(flowData) => <Inner flowSettings={flowSettings} flowData={flowData} />}
				</Async>
			)}
		</Async>
	);
});

interface InnerProps {
	flowSettings: CreateNewMessageSettings;
	flowData: CreateNewMessageFlowData;
}

function Inner({ flowSettings, flowData }: InnerProps) {
	const createNewMessageController = useResolveController(CreateNewMessageController);
	const controller = useResolveController(ScreenConclusionController);
	const basketService = useResolve(BasketService);
	const fileManagerService = useResolve(FileManagerService);
	const [payment, setPayment] = useState<{ paymentBillId: number; paymentAmount: string; commitJobId: number } | null>(null);
	const [disableSubmit, setDisableSubmit] = useState<string | false>(false);
	const analytic = useAnalyticContext();

	const selectedRfv = createNewMessageController.getSelectedReasonForVisit();

	function handleFileManagerStateChange(state: FileManagerServiceReadyState) {
		setDisableSubmit(
			state === FileManagerServiceReadyState.NotReady
				? withTranslation('View.CreateNewMessage.ScreenConclusion.AttachedFilesNotReady')
				: false,
		);
	}

	async function handleSubmit(data: CellulaFormSubmitData) {
		analytic.trackEvent('Clicked', 'Submit button');

		const submitResult = await controller.submit({
			...data,
			files: fileManagerService.peakUploadedWorkers(),
		});

		if (submitResult.err) {
			if (submitResult.err instanceof NonRecoverableError) {
				createNewMessageController.goToScreen('error');

				return;
			}

			return Err(submitResult.err);
		}

		if (submitResult.ok?.commitJobId && submitResult.ok?.paymentBillId && flowData?.price) {
			setPayment({
				paymentBillId: submitResult.ok.paymentBillId,
				paymentAmount: flowData.price,
				commitJobId: submitResult.ok.commitJobId,
			});
		} else {
			createNewMessageController.goToScreen('done', {
				conversationId: submitResult.ok?.messageConversationId ?? undefined,
			});
		}

		return Ok();
	}

	async function handlePaymentSuccess() {
		if (!payment?.commitJobId) {
			createNewMessageController.goToScreen('error');

			return;
		}

		const paymentResult = await controller.waitForCommitStatus(payment.commitJobId);
		if (paymentResult.err) {
			createNewMessageController.goToScreen('error');

			return;
		}

		createNewMessageController.goToScreen('done', {
			conversationId: paymentResult.ok?.messageConversationId ?? undefined,
		});

		return Ok();
	}

	function handlePaymentAbort() {
		setPayment(null);
	}

	const showMessageTextArea = !flowSettings.predefinedDescriptionOfMatter;
	const showAttachFiles = flowSettings.attachFiles;
	const showDetails = flowData.price || selectedRfv || basketService.formManager.getForms().length;
	const emptyDescription =
		showMessageTextArea || showAttachFiles || showDetails
			? undefined
			: withTranslation('View.CreateNewMessage.ScreenConclusion.EmptyDescription');

	return (
		<>
			<TopBar.Content goBack />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Title
					title={withTranslation('View.CreateNewMessage.ScreenConclusion.Title')}
					titleSize='small'
					description={emptyDescription}
				/>

				<Flex grow='1' gap={tokens.size.x3}>
					<Form.Root onSubmit={handleSubmit} options={{}}>
						{showMessageTextArea || showAttachFiles ? (
							<Flex gap={tokens.size.x2}>
								{showMessageTextArea ? (
									<div>
										<InputTextArea
											name='message'
											label={withTranslation('View.CreateNewMessage.ScreenConclusion.Input.Label.Reason')}
											minHeight={120}
											maxHeight={400}
											autoHeight
											required
											characterCount={REASON_MAX_LENGTH}
											options={{
												validate: {
													required: (v) =>
														validateRequired(v) ||
														withTranslation('View.CreateNewMessage.ScreenConclusion.ValidationError.Required.Reason'),
												},
												maxLength: {
													value: REASON_MAX_LENGTH,
													message: withTranslation('View.CreateNewMessage.ScreenConclusion.ValidationError.MaxLength.Reason', {
														maxLength: `${REASON_MAX_LENGTH}`,
													}),
												},
											}}
										/>
									</div>
								) : null}

								{showAttachFiles ? (
									<Flex gap={tokens.size.x1}>
										<fieldset style={{ display: 'contents' }}>
											<Label type='legend' optional>
												{withTranslation('Form.Input.Label.AttachedFiles')}
											</Label>
											<FileUpload.Root identityEndpointPath='client/v2/userfiles' onStateChange={handleFileManagerStateChange}>
												<FileUpload.List />
												<FileUpload.DropZone>
													<FileUpload.Button />
												</FileUpload.DropZone>
											</FileUpload.Root>
										</fieldset>
									</Flex>
								) : null}
							</Flex>
						) : null}

						{showDetails ? (
							<DataList.Root>
								{flowData.price ? (
									<DataList.Item label={withTranslation('Common.Label.Cost')}>
										<BadgeText variant='info'>{flowData.price}</BadgeText>
									</DataList.Item>
								) : null}
								{selectedRfv ? (
									<DataList.Item label={withTranslation('Common.Label.Rfv')}>{selectedRfv?.Name ?? ''}</DataList.Item>
								) : null}
								{basketService.formManager.getForms().length > 0 ? (
									<DataList.Item label={withTranslation('Common.Label.AnsweredForms')} direction='vertical'>
										<Flex gap={tokens.spacing.x1}>
											{basketService.formManager.getForms().map((form) => (
												<FormCard key={form.formId} formWorker={form} analyticAction='' onCompleted={false} />
											))}
										</Flex>
									</DataList.Item>
								) : null}
							</DataList.Root>
						) : null}

						<Form.Error />

						<StyledBottomSection grow='1' justify='flex-end' gap={tokens.spacing.x2}>
							<div>
								{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part1')}{' '}
								<IframeSheet
									src={controller.termsOfServiceUrl.value ?? ''}
									trigger={
										<TextLink as='button' size='small'>
											{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Link')}
										</TextLink>
									}
								/>{' '}
								{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part2')}
							</div>
							<ButtonArrange>
								<Form.SubmitButton disabled={!!disableSubmit} disabledText={disableSubmit || ''}>
									{flowData.requirePayment
										? withTranslation('View.CreateNewMessage.ScreenConclusion.ButtonWithPayment')
										: withTranslation('View.CreateNewMessage.ScreenConclusion.ButtonWithoutPayment')}
								</Form.SubmitButton>
							</ButtonArrange>
						</StyledBottomSection>
					</Form.Root>
				</Flex>
			</Flex>
			{payment ? (
				<PaymentSheet
					billId={payment.paymentBillId}
					paymentAmount={payment.paymentAmount}
					onSuccess={handlePaymentSuccess}
					onAbort={handlePaymentAbort}
				/>
			) : null}
		</>
	);
}

interface Props {
	billId: number;
	paymentAmount: string;
	onSuccess?: () => void;
	onAbort?: () => void;
}

function PaymentSheet({ paymentAmount, billId, onSuccess, onAbort }: Props): JSX.Element {
	return (
		<Sheet onClose={onAbort} defaultOpen variant='full-restricted'>
			<Payment billId={billId} paymentAmount={paymentAmount} onSuccess={onSuccess} onAbort={onAbort} />
		</Sheet>
	);
}
